import Utils from '../helpers/utils';
import RequestResponseHelpers from '../helpers/RequestResponseHelpers';
import { X_TRACE_ID_HEADER } from '../config';
import { setServerLog } from '../helpers/ServerLogStorageUtil';
import axios from 'axios';
import deepmerge from 'deepmerge';
import axiosRetry from 'axios-retry';

export class BaseClient {
  constructor(config) {
    this.config = config || {};
    this.config.headers = deepmerge({}, this.config.headers || {});
    this.baseUrl = this.config.baseUrl || '';
    this.isServer = this.config.isServer;
    this.logger = this.config.logger || console;
    this.traceIdHeader = X_TRACE_ID_HEADER;
    this._httpClient = null;
    this._sendLogToServerClient = null;
    this.clientName = this.config.clientName || this._getClientName();
  }

  _getClientName() {
    let client = 'SUB-AUTH-WEBAPP';
    if (!Utils.isServerSide() && window._nhstRequestClientName) {
      client = window._nhstRequestClientName;
    }
    return client;
  }

  getClientName() {
    return this.clientName;
  }

  setClientName(clientName) {
    this.clientName = clientName;
  }

  getTraceId() {
    return this.config.headers[this.traceIdHeader];
  }

  setTraceId(traceId) {
    this.config.headers[this.traceIdHeader] = traceId;
  }

  get httpClient() {
    if (!this._httpClient) {
      this._httpClient = this.createClient(false);
    }
    return this._httpClient;
  }

  get sendLogToServerClient() {
    if (!this._sendLogToServerClient) {
      this._sendLogToServerClient = this.createClient(true);
    }
    return this._sendLogToServerClient;
  }

  _updateResponseConfig(config) {
    const conf = config || {};
    const metadata = conf.metadata || {};
    const endTime = new Date();
    const startTime = metadata.startTime || endTime;
    return {
      ...metadata,
      startTime,
      endTime,
      duration: endTime - startTime,
    };
  }

  createClient(minimumConfig = false) {
    const api = axios.create({
      baseURL: this.baseUrl,
      xsrfCookieName: 'CSRF-TOKEN',
      xsrfHeaderName: 'X-CSRF-TOKEN',
      headers: {
        Accept: 'application/json',
        ...this.config.headers
      }
    });

    api.defaults.withCredentials = false;

    if (minimumConfig) {
      return api;
    }

    api.interceptors.request.use(
      config => {
        const newConfig = { ...config };
        newConfig.headers = newConfig.headers || {};
        RequestResponseHelpers.generateClientSideCsrf(newConfig);
        RequestResponseHelpers.addRecaptchaTokenHeader(newConfig);
        RequestResponseHelpers.addTraceIdHeader(newConfig);
        RequestResponseHelpers.addIdempotentHeader(newConfig);
        RequestResponseHelpers.addRandomQueryParamToBypassCache(newConfig);
        newConfig.metadata = {
          ...(newConfig.metadata || {}),
          startTime: new Date(),
          requestHeaders: RequestResponseHelpers.whiteListedHeaders(newConfig.headers),
        };
        newConfig.metadata.tries = newConfig.metadata.tries || 0;
        newConfig.metadata.tries += 1;
        return newConfig;
      },
      error => {
        this.logger.error('[Error in creating Request]');
        this.logger.error(error);
        return Promise.reject(error);
      }
    );

    api.interceptors.response.use(
      async response => {
        const resp = { ...response, ...this._updateResponseConfig(response.config) };
        await this.logResponse(resp);
        return resp;
      },
      async error => {
        const newError = { ...error };
        // Comment out the next line for debugging purpose
        // this.logger.error(error);
        newError.response = { ...newError.response, ...this._updateResponseConfig(error.config) };
        await this.logResponse(newError, true);
        return Promise.reject(newError);
      }
    );
    axiosRetry(api, { retries: 0 });
    return api;
  }

  isAppDevice() {
    return Utils.isAppDevice();
  }

  generateLogMessage(response = {}) {
    let { baseURL, url, method, data = {}, params = {}, extraInfo = {}, headers = {} } = response.config || {};
    const request = response.request || {};
    const generatedRequest = {
      path: request.path,
      method: request.method,
      responseURL: request.responseURL
    };
    let userAgent = '[server]';
    if (!Utils.isServerSide()) {
      userAgent = '[web]';
      try {
        if (this.isAppDevice()) {
          userAgent = `[app device]`;
        }
        userAgent += ' ' + navigator.userAgent;
      } catch (e) {
        console.error('Could not determine user agent', e);
      }
    }
    data = Utils.parseToJSON(data);
    data = RequestResponseHelpers.hideSensitiveData(data);
    params = RequestResponseHelpers.hideSensitiveData(params);
    extraInfo = RequestResponseHelpers.hideSensitiveData(extraInfo);
    headers = response.requestHeaders || RequestResponseHelpers.whiteListedHeaders(headers);
    const responseHeaders = RequestResponseHelpers.whiteListedHeaders(response.headers || {});
    let title = '2xx response found';
    const isSuccessResponse = RequestResponseHelpers.isSuccessResponse(response);
    if (!isSuccessResponse) {
      title = 'Non 2xx response found';
    }
    const reqStartTime = response.startTime;
    const duration = response.duration;
    const tries = response.tries || '-1';
    const requestUrl = `${baseURL}${url}`;

    const logMsg = `
[${method}] ${requestUrl}

${title}

Request Start time: ${reqStartTime}

Duration: ${duration / 1000} seconds

Tries
${tries}

Params
${JSON.stringify(params, 0, 2)}

Request Data
${JSON.stringify(data, 0, 2)}

Request Headers
${JSON.stringify(headers, 0, 2)}

Extra:
${JSON.stringify(extraInfo, 0, 2)}

Generated Request
${JSON.stringify(generatedRequest, 0, 2)}

Response Code
${response.status}

Response Content
${JSON.stringify(response.data, 0, 2)}

Response Headers
${JSON.stringify(responseHeaders, 0, 2)}

User Agent
${userAgent}
`;
    return logMsg;
  }

  async logResponse(result = {}, isError = false) {
    let log = '';
    let errTitle = '';
    const config = result.config;
    if (this.isServer || !this.shouldSendLogToServer(config, isError)) {
      return;
    }
    const minimumReqConfig = {
      config,
      ...(result.response || {})
    };
    if (result.toJSON) {
      this.logger.info('JSON RESULT -> ', result.toJSON());
    }
    if (isError) {
      // errors that have response code
      if (result.response && result.response.config) {
        log = `[Error in Response] ${this.generateLogMessage(result.response)}`;
      } else if (result.request) {
        errTitle = '[Error in Request]';
        this.logger.error(errTitle);
        this.logger.error(result);
        this.logger.error(result.request);
        log = this.generateLogMessage(minimumReqConfig);
        log = `${errTitle}\nConfig Sent: ${log}\nAxios Result Request: ${result.request} ${JSON.stringify(result.request, 0, 2)}`;
      } else {
        errTitle = '[Something weird has happened]';
        this.logger.error(errTitle);
        this.logger.error(result);
        log = this.generateLogMessage(minimumReqConfig);
        log = `${errTitle}\nConfig Sent: ${log}\nAxios Result: ${result} ${JSON.stringify(result, 0, 2)}`;
      }
    } else {
      log = this.generateLogMessage(result);
    }
    const response = isError ? result.response || {} : result;
    const logger = isError ? this.logger.error : this.logger.info;
    const level = isError ? 'ERROR' : 'INFO';
    logger(this.isServer ? log : response);
    const headers = config.headers || {};
    await this.serverLog({ message: log, level, traceId: headers['X-Trace-Id'] });
  }

  // more info can be passed in extraInfo which is a dict/map (config: {extraInfo: {abc: def}})
  async send(config) {
    try {
      return this.httpClient(config);
    } catch (e) {
      this.logger.error(e);
      throw e;
    }
  }

  post(config) {
    config.method = 'POST';
    return this.send(config);
  }

  put(config) {
    config.method = 'PUT';
    return this.send(config);
  }

  delete(config) {
    config.method = 'DELETE';
    return this.send(config);
  }

  get(config) {
    config.method = 'GET';
    return this.send(config);
  }

  async serverLog(conf = {}) {
    const config = this.generateRequestConfigForSendingLogToServer(conf);
    const extraHeaders = {};
    extraHeaders[this.traceIdHeader] = config.traceId;
    const headers = Object.assign({}, conf.headers, extraHeaders);
    const request = { ...config, headers, method: 'POST' };
    RequestResponseHelpers.generateClientSideCsrf(request);
    this.logger.info('Sending log to server');
    try {
      const resp = await this.sendLogToServerClient(request);
      return resp.data;
    } catch (err) {
      if (err.response) {
        this.logger.error(`Failed to send server log. Request: ${JSON.stringify(request)} Status: ${err.response.status}`);
      }
      setServerLog(request);
    }
  }

  shouldSendLogToServer() {
    return false;
  }

  generateRequestConfigForSendingLogToServer(config) {
    throw new Error('You need to implement this in the sub classes!');
  }
}
